import { jsxs, jsx } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Box, Text, IconChevron, Stack, RadioGroup, RadioItem, Divider, Columns, Column, TextField, IconCritical, Notice } from "braid-design-system";
import { format, parse, isValid, startOfDay, endOfDay, differenceInDays, subDays } from "date-fns";
import { useCallback, useState, useRef, useMemo, useEffect } from "react";
import { absoluteDateFormatter } from "@seek/unified-display-date-time";
import { enAU, enNZ, id, th, zhHK } from "date-fns/locale";
import { BaseCalendar } from "../BaseCalendar/BaseCalendar.mjs";
import { translations } from "../../../components/CalendarField/.vocab/index.mjs";
import { dropdown, root } from "./CalendarField.css.mjs";
const localeMap = {
  "en-AU": enAU,
  "en-HK": enAU,
  "en-ID": enAU,
  "en-MY": enAU,
  "en-NZ": enNZ,
  "en-PH": enAU,
  "en-SG": enAU,
  "en-TH": enAU,
  "id-ID": id,
  "th-TH": th,
  "zh-HK": zhHK
};
const formatDate = ({
  date,
  dateFormat = "dd MMM yyyy",
  locale = "en-AU"
}) => {
  if (!date) {
    return "";
  }
  return format(date, dateFormat, { locale: localeMap[locale] });
};
const formatDateWithUnifiedDisplay = ({
  date,
  locale = "en-AU"
}) => {
  const timezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  const unifiedFormattedDate = absoluteDateFormatter({
    date: new Date(date),
    format: "short",
    timezone,
    locale
  });
  return unifiedFormattedDate.isValid ? unifiedFormattedDate.formattedDate : formatDate({ date });
};
const getKeys = Object.keys;
const normalizeKey = ({
  key,
  keyCode
}) => {
  if (keyCode >= 37 && keyCode <= 40 && key.indexOf("Arrow") !== 0) {
    return `Arrow${key}`;
  }
  if (keyCode === 27) {
    return "Escape";
  }
  return key;
};
const formatDateForCalendar = (date, locale = "en-AU") => {
  if (!date) {
    return "";
  }
  return formatDate({ date, dateFormat: "yyyy-MM-dd", locale });
};
const formatDateForCalendarLabel = (date, locale = "en-AU") => {
  if (!date) {
    return "";
  }
  return formatDateWithUnifiedDisplay({
    date,
    locale
  });
};
const formatDateForCalendarInput = (date, locale = "en-AU") => {
  if (!date) {
    return "";
  }
  return formatDate({
    date,
    dateFormat: "dd/MM/yyyy",
    locale
  });
};
const roundDateFunction = {
  start: startOfDay,
  end: endOfDay
};
const parseDateForCalendar = (dateString, type) => {
  const date = parse(dateString, "dd/MM/yyyy", /* @__PURE__ */ new Date());
  if (isValid(date)) {
    return roundDateFunction[type](date);
  }
  const anyFormatDate = new Date(dateString);
  if (!isNaN(anyFormatDate.getTime())) {
    return roundDateFunction[type](anyFormatDate);
  }
  return null;
};
const useCalendarValidator = (minDate, maxDate) => {
  const isMoreThanNDays = useCallback(
    (numOfDays) => {
      const fromDate = new Date(minDate);
      const toDate = new Date(maxDate);
      return Math.abs(differenceInDays(fromDate, toDate)) >= numOfDays;
    },
    [minDate, maxDate]
  );
  const isBeforeMinDate = useCallback(
    (date) => new Date(date).getTime() < new Date(minDate).getTime(),
    [minDate]
  );
  const isAfterMaxDate = useCallback(
    (date) => new Date(date).getTime() > new Date(maxDate).getTime(),
    [maxDate]
  );
  return {
    isMoreThanNDays,
    isBeforeMinDate,
    isAfterMaxDate
  };
};
const useCalendarHelper = (minDate, maxDate) => {
  const { isBeforeMinDate, isAfterMaxDate } = useCalendarValidator(
    minDate,
    maxDate
  );
  const setStartOfDay = useCallback(
    (currentStartDate) => isBeforeMinDate(currentStartDate) ? minDate : currentStartDate,
    [minDate, isBeforeMinDate]
  );
  const setEndOfDay = useCallback(
    (currentEndDate) => isAfterMaxDate(currentEndDate) ? maxDate : currentEndDate,
    [maxDate, isAfterMaxDate]
  );
  return {
    setStartOfDay,
    setEndOfDay
  };
};
const datePresetValues = {
  "Last 7 days": 7,
  "Last 30 days": 30,
  "Last 90 days": 90,
  "Last 365 days": 365
};
const CalendarField = ({
  dateValues: defaultDateValues,
  updateDateValues,
  minDate,
  maxDate,
  noticeMessage,
  locale = "en-AU"
}) => {
  const { t } = useTranslations(translations);
  const {
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    datePreset: defaultDatePreset
  } = defaultDateValues;
  const invalidDateFormatErrorMessage = t("Invalid date format error message");
  const endDateEarlyThanStartDateErrorMessage = t(
    "To date before from date error message"
  );
  const startDateLaterThanEndDateErrorMessage = t(
    "From date after to date error message"
  );
  const [calendarDate, setCalendarDate] = useState(
    defaultEndDate || maxDate
  );
  const [startDateLabel, setStartDateLabel] = useState("");
  const [endDateLabel, setEndDateLabel] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [dateInvalidErrorMessage, setDateInvalidErrorMessage] = useState("");
  const [startDateIsInvalid, setStartDateIsInvalid] = useState(false);
  const [endDateIsInvalid, setEndDateIsInvalid] = useState(false);
  const [disableEndDate, setDisableEndDate] = useState(false);
  const [disableStartDate, setDisableStartDate] = useState(false);
  const [dateValues, setDateValues] = useState(defaultDateValues);
  const startDateFieldRef = useRef(null);
  const endDateFieldRef = useRef(null);
  const { startDate, endDate, datePreset } = dateValues;
  const { isMoreThanNDays, isBeforeMinDate, isAfterMaxDate } = useCalendarValidator(minDate, maxDate);
  const { setStartOfDay, setEndOfDay } = useCalendarHelper(minDate, maxDate);
  const validDatePresets = useMemo(
    () => getKeys(datePresetValues).filter(
      (dp) => isMoreThanNDays(datePresetValues[dp])
    ),
    [isMoreThanNDays]
  );
  const dateOutOfRangeErrorMessage = useMemo(
    () => t("Date out of range error message", {
      from: formatDate({
        date: minDate,
        dateFormat: "dd/MM/yyyy",
        locale
      }),
      to: formatDate({
        date: maxDate,
        dateFormat: "dd/MM/yyyy",
        locale
      })
    }),
    [minDate, maxDate, locale, t]
  );
  const displayedDateLabel = () => dateLabel;
  function handleDateSelection(dateOption, dateSelected) {
    if (dateSelected) {
      if (startDate && endDate) {
        setDateValues({
          startDate: setStartOfDay(startOfDay(dateSelected)),
          endDate: null,
          datePreset: null
        });
      } else if (startDate && dateSelected.getTime() >= startDate.getTime()) {
        updateDateValues({
          startDate,
          endDate: setEndOfDay(endOfDay(dateSelected)),
          datePreset: null
        });
      } else if (startDate && dateSelected.getTime() <= startDate.getTime()) {
        updateDateValues({
          startDate: setStartOfDay(startOfDay(dateSelected)),
          endDate: setEndOfDay(endOfDay(startDate)),
          datePreset: null
        });
      }
    } else if (dateOption) {
      updateDateValues({
        datePreset: dateOption,
        startDate: setStartOfDay(
          startOfDay(subDays(maxDate, datePresetValues[dateOption] - 1))
        ),
        endDate: maxDate
      });
    }
  }
  const onCalendarDateSelect = (dateString) => {
    handleDateSelection(null, new Date(dateString));
  };
  const expandCollapseHandler = () => {
    if (isOpen && !endDate) {
      updateDateValues({
        datePreset: null,
        startDate: setStartOfDay(startOfDay(startDate)),
        endDate: setEndOfDay(endOfDay(startDate))
      });
    }
    setIsOpen((current) => !current);
  };
  const handleStartDateInput = (e) => {
    setStartDateLabel(e.target.value);
  };
  const handleEndDateInput = (e) => {
    setEndDateLabel(e.target.value);
  };
  const handleStartDateOffFocus = (value) => {
    if (value === "") {
      const newStartDateLabel = formatDateForCalendarLabel(startDate, locale);
      setStartDateLabel(newStartDateLabel);
      setDateInvalidErrorMessage("");
      setStartDateIsInvalid(false);
      setDisableEndDate(false);
      return;
    }
    let newStartDate = parseDateForCalendar(value, "start");
    if (!newStartDate) {
      setDateInvalidErrorMessage(invalidDateFormatErrorMessage);
      setStartDateIsInvalid(true);
      setDisableEndDate(true);
      return;
    }
    const isStartDateTooEarly = isBeforeMinDate(newStartDate);
    const isStartDateInFuture = isAfterMaxDate(newStartDate);
    const isSameAsStartDate = newStartDate.getDay() === minDate.getDay() && newStartDate.getMonth() === minDate.getMonth() && newStartDate.getFullYear() === minDate.getFullYear();
    if (isStartDateTooEarly) {
      if (!isSameAsStartDate) {
        setDateInvalidErrorMessage(dateOutOfRangeErrorMessage);
        setStartDateIsInvalid(true);
        setDisableEndDate(true);
        return;
      }
      newStartDate = setStartOfDay(newStartDate);
    }
    if (isStartDateInFuture) {
      setDateInvalidErrorMessage(dateOutOfRangeErrorMessage);
      setStartDateIsInvalid(true);
      setDisableEndDate(true);
      return;
    }
    const isStartDateAfterEndDate = endDate && newStartDate.getTime() > endDate.getTime();
    if (isStartDateAfterEndDate) {
      setDateInvalidErrorMessage(startDateLaterThanEndDateErrorMessage);
      setEndDateIsInvalid(true);
      setDisableStartDate(true);
      return;
    }
    setDateInvalidErrorMessage("");
    setStartDateIsInvalid(false);
    setDisableEndDate(false);
    if (endDate) {
      updateDateValues({
        startDate: newStartDate,
        endDate,
        datePreset: null
      });
    } else {
      setDateValues({
        startDate: newStartDate,
        endDate,
        datePreset: null
      });
    }
  };
  const handleEndDateOffFocus = (value) => {
    if (value === "") {
      const newEndDateLabel = formatDateForCalendarLabel(endDate, locale);
      setEndDateLabel(newEndDateLabel);
      setDateInvalidErrorMessage("");
      setEndDateIsInvalid(false);
      setDisableStartDate(false);
      return;
    }
    let newEndDate = parseDateForCalendar(value, "end");
    if (!newEndDate) {
      setDateInvalidErrorMessage(invalidDateFormatErrorMessage);
      setEndDateIsInvalid(true);
      setDisableStartDate(true);
      return;
    }
    const isEndDateTooEarly = isBeforeMinDate(newEndDate);
    const isEndDateInFuture = isAfterMaxDate(newEndDate);
    const isSameAsEndDate = newEndDate.getDay() === maxDate.getDay() && newEndDate.getMonth() === maxDate.getMonth() && newEndDate.getFullYear() === maxDate.getFullYear();
    if (isEndDateTooEarly) {
      setDateInvalidErrorMessage(dateOutOfRangeErrorMessage);
      setEndDateIsInvalid(true);
      setDisableStartDate(true);
      return;
    }
    if (isEndDateInFuture) {
      if (!isSameAsEndDate) {
        setDateInvalidErrorMessage(dateOutOfRangeErrorMessage);
        setEndDateIsInvalid(true);
        setDisableStartDate(true);
        return;
      }
      newEndDate = setEndOfDay(newEndDate);
    }
    const isEndDateBeforeStartDate = newEndDate.getTime() < startDate.getTime();
    if (isEndDateBeforeStartDate) {
      setDateInvalidErrorMessage(endDateEarlyThanStartDateErrorMessage);
      setEndDateIsInvalid(true);
      setDisableStartDate(true);
      return;
    }
    setDateInvalidErrorMessage("");
    setEndDateIsInvalid(false);
    setCalendarDate(newEndDate);
    setDisableStartDate(false);
    updateDateValues({
      startDate,
      endDate: newEndDate,
      datePreset: null
    });
  };
  const onStartDateInputBlur = (event) => {
    handleStartDateOffFocus(event.target.value);
  };
  const onEndDateInputBlur = (event) => {
    handleEndDateOffFocus(event.target.value);
  };
  const onStartDateInputFocus = () => {
    setTimeout(
      () => setStartDateLabel(formatDateForCalendarInput(startDate)),
      100
    );
  };
  const onEndDateInputFocus = () => {
    setTimeout(() => setEndDateLabel(formatDateForCalendarInput(endDate)), 100);
  };
  const onMonthClick = (date) => {
    setCalendarDate(new Date(date));
  };
  const translateLabel = useCallback(
    (label) => {
      switch (label) {
        case "Last 7 days":
        case "Last 30 days":
        case "Last 90 days":
        case "Last 365 days":
          return t("Last N days", { number: `${datePresetValues[label]}` });
        default:
          return t("Last N days", { number: "30" });
      }
    },
    [t]
  );
  const startDateFieldProps = {
    onKeyDown: (event) => {
      var _a, _b;
      const targetKey = normalizeKey(event);
      switch (targetKey) {
        case "Escape": {
          event.preventDefault();
          (_a = startDateFieldRef.current) == null ? void 0 : _a.blur();
          handleStartDateOffFocus("");
          return;
        }
        case "Enter": {
          event.preventDefault();
          (_b = startDateFieldRef.current) == null ? void 0 : _b.blur();
          handleStartDateOffFocus(startDateLabel);
          return;
        }
      }
    }
  };
  const endDateFieldProps = {
    onKeyDown: (event) => {
      var _a, _b;
      const targetKey = normalizeKey(event);
      switch (targetKey) {
        case "Escape": {
          event.preventDefault();
          (_a = endDateFieldRef.current) == null ? void 0 : _a.blur();
          handleEndDateOffFocus("");
          return;
        }
        case "Enter": {
          event.preventDefault();
          (_b = endDateFieldRef.current) == null ? void 0 : _b.blur();
          handleEndDateOffFocus(endDateLabel);
          return;
        }
      }
    }
  };
  const dateLabel = useMemo(() => {
    if (startDate && !endDate) {
      return t("Start date to", {
        startDate: formatDateForCalendarLabel(startDate, locale)
      });
    }
    if (startDateLabel === endDateLabel) {
      return t("Same start and end date", {
        date: formatDateForCalendarLabel(startDate, locale)
      });
    }
    if (startDate && endDate) {
      return t("Start date to end date", {
        startDate: formatDateForCalendarLabel(startDate, locale),
        endDate: formatDateForCalendarLabel(endDate, locale)
      });
    }
    return "";
  }, [startDate, endDate, startDateLabel, endDateLabel, locale, t]);
  const resetErrorState = () => {
    setDateInvalidErrorMessage("");
    setStartDateIsInvalid(false);
    setEndDateIsInvalid(false);
    setDisableStartDate(false);
    setDisableEndDate(false);
  };
  useEffect(() => {
    const newStartDateLabel = formatDateForCalendarLabel(startDate, locale);
    const newEndDateLabel = formatDateForCalendarLabel(endDate, locale);
    setStartDateLabel(newStartDateLabel);
    setEndDateLabel(newEndDateLabel);
    resetErrorState();
  }, [startDate, endDate, locale]);
  useEffect(() => {
    setDateValues({
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      datePreset: defaultDatePreset
    });
  }, [defaultDatePreset, defaultEndDate, defaultStartDate]);
  return /* @__PURE__ */ jsxs(Box, { "data-id": "component-calendar", children: [
    /* @__PURE__ */ jsxs(
      Box,
      {
        "data-id": "date-dropdown-trigger",
        data: { testid: "date-dropdown-trigger" },
        background: "surface",
        borderRadius: "large",
        boxShadow: "borderNeutralLight",
        paddingX: "medium",
        paddingY: "small",
        cursor: "pointer",
        width: "full",
        onClick: expandCollapseHandler,
        display: "flex",
        justifyContent: "spaceBetween",
        alignItems: "center",
        children: [
          /* @__PURE__ */ jsx(Text, { children: displayedDateLabel() }),
          /* @__PURE__ */ jsx(
            IconChevron,
            {
              tone: "secondary",
              size: "standard",
              direction: isOpen ? "up" : "down"
            }
          )
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsx(
      Box,
      {
        bottom: 0,
        cursor: "default",
        display: "block",
        left: 0,
        position: "fixed",
        right: 0,
        top: 0,
        zIndex: "dropdownBackdrop",
        onClick: expandCollapseHandler,
        "data-id": "calendar-backdrop"
      }
    ),
    isOpen && /* @__PURE__ */ jsx(Box, { position: "relative", zIndex: "modal", className: dropdown, children: /* @__PURE__ */ jsx(
      Box,
      {
        "data-id": "calendar-modal",
        background: "surface",
        marginTop: "xxsmall",
        padding: "medium",
        position: "absolute",
        zIndex: "modal",
        borderRadius: "standard",
        boxShadow: "borderNeutralLight",
        width: "full",
        children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
          /* @__PURE__ */ jsx(
            RadioGroup,
            {
              id: "calendar-date-presets",
              value: datePreset || "",
              onChange: ({ currentTarget: { value } }) => handleDateSelection(value),
              label: "",
              children: validDatePresets.map((dp) => /* @__PURE__ */ jsx(RadioItem, { label: translateLabel(dp), value: dp }, dp))
            }
          ),
          isMoreThanNDays(7) ? /* @__PURE__ */ jsx(Divider, {}) : null,
          isMoreThanNDays(7) ? /* @__PURE__ */ jsxs(Text, { size: "standard", children: [
            t("Or choose your own dates"),
            ":"
          ] }) : null,
          /* @__PURE__ */ jsxs(Columns, { space: "small", alignY: "center", children: [
            /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
              /* @__PURE__ */ jsxs(Text, { size: "small", children: [
                t("From date indicator"),
                ":"
              ] }),
              /* @__PURE__ */ jsx(
                Box,
                {
                  "data-id": "start-date-box",
                  zIndex: "dropdown",
                  background: "surface",
                  borderRadius: "standard",
                  ...startDateFieldProps,
                  children: /* @__PURE__ */ jsx(
                    TextField,
                    {
                      id: "start-date-field",
                      data: { testid: "start-date-field" },
                      "aria-label": "StartDate",
                      ref: startDateFieldRef,
                      type: "text",
                      value: startDateLabel,
                      onChange: handleStartDateInput,
                      onBlur: onStartDateInputBlur,
                      placeholder: "dd/mm/yyyy",
                      onFocus: onStartDateInputFocus,
                      tone: startDateIsInvalid ? "critical" : "neutral",
                      disabled: disableStartDate
                    }
                  )
                }
              )
            ] }) }),
            /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
              /* @__PURE__ */ jsxs(Text, { size: "small", children: [
                t("To date indicator"),
                ":"
              ] }),
              /* @__PURE__ */ jsx(
                Box,
                {
                  "data-id": "end-date-box",
                  zIndex: "dropdown",
                  background: "surface",
                  borderRadius: "standard",
                  ...endDateFieldProps,
                  children: /* @__PURE__ */ jsx(
                    TextField,
                    {
                      id: "end-date-field",
                      data: { testid: "end-date-field" },
                      "aria-label": "EndDate",
                      ref: endDateFieldRef,
                      type: "text",
                      value: endDateLabel,
                      onChange: handleEndDateInput,
                      onBlur: onEndDateInputBlur,
                      onFocus: onEndDateInputFocus,
                      placeholder: "dd/mm/yyyy",
                      tone: endDateIsInvalid ? "critical" : "neutral",
                      disabled: disableEndDate
                    }
                  )
                }
              )
            ] }) })
          ] }),
          Boolean(dateInvalidErrorMessage) && /* @__PURE__ */ jsx(Text, { tone: "critical", icon: /* @__PURE__ */ jsx(IconCritical, {}), size: "xsmall", children: dateInvalidErrorMessage }),
          /* @__PURE__ */ jsx(
            BaseCalendar,
            {
              currentLocalDate: calendarDate,
              maxDate: formatDateForCalendar(maxDate, locale),
              minDate: formatDateForCalendar(minDate, locale),
              onDateButtonClicked: onCalendarDateSelect,
              onMonthClicked: onMonthClick,
              value: {
                endDate: endDate ? formatDateForCalendar(endDate, locale) : "",
                startDate: startDate ? formatDateForCalendar(startDate, locale) : ""
              },
              customRootClass: root
            }
          ),
          noticeMessage ? /* @__PURE__ */ jsx(Notice, { tone: "info", children: /* @__PURE__ */ jsx(Box, { style: { marginTop: -2 }, children: /* @__PURE__ */ jsx(Text, { children: noticeMessage }) }) }) : null
        ] })
      }
    ) })
  ] });
};
export {
  CalendarField
};
